"use client";

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

function useInView(threshold = 0.12) {
  const ref = useRef<HTMLElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

interface FormData {
  projectType: string;
  spaceType: string;
  spaceSize: string;
  budget: string;
  timeline: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  message: string;
}

const projectTypes = ['住宅新成屋客變', '老屋翻新', '預售屋客變', '系統家具整合', '咖啡廳/餐廳', '診所/醫療空間', '辦公室', '品牌展廳', '其他商業空間'];
const spaceSizes = ['30㎡ 以下', '30–60㎡', '60–100㎡', '100–150㎡', '150㎡ 以上'];
const budgets = ['HKD 50,000 以下', 'HKD 50,000–100,000', 'HKD 100,000–200,000', 'HKD 200,000–400,000', 'HKD 400,000 以上', '待討論'];
const timelines = ['1個月內', '1–3個月', '3–6個月', '6個月以上', '尚未確定'];

export default function RegistrationCenter() {
  const formRef = useInView();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    projectType: '',
    spaceType: '',
    spaceSize: '',
    budget: '',
    timeline: '',
    name: '',
    phone: '',
    email: '',
    address: '',
    message: '',
  });

  const update = (field: keyof FormData, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Mock submit handler — connect to backend here
    setSubmitted(true);
  };

  const canProceedStep1 = formData.projectType !== '';
  const canProceedStep2 = formData.spaceSize !== '' && formData.budget !== '' && formData.timeline !== '';
  const canSubmit = formData.name !== '' && formData.phone !== '' && formData.email !== '';

  return (
    <>
      <Header />

      {/* ── LIGHTWEIGHT HERO ─────────────────────────────── */}
      <section className="pt-32 pb-16 bg-primary relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(201,169,110,0.4) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="relative z-10 max-w-container mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-mono text-xs text-accent/70 tracking-widest uppercase">Registration Center</span>
          </div>
          <h1 className="font-heading font-700 text-white mb-3" style={{ fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: '1.05' }}>
            報名諮詢中心
          </h1>
          <p className="font-body text-base text-white/50 max-w-lg">
            填寫以下表格，我們將在2個工作日內與你聯繫，安排免費初步諮詢。
          </p>
        </div>
      </section>

      {/* ── FORM ─────────────────────────────────────────── */}
      <section
        ref={formRef.ref as React.RefObject<HTMLElement>}
        className="py-20 bg-background"
      >
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto">
            {!submitted ? (
              <>
                {/* Step indicator */}
                <div
                  className="flex items-center gap-0 mb-12"
                  style={{
                    opacity: formRef.inView ? 1 : 0,
                    transform: formRef.inView ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'opacity 0.6s ease, transform 0.6s ease',
                  }}
                >
                  {[1, 2, 3].map((s, i) => (
                    <React.Fragment key={s}>
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 flex items-center justify-center font-mono text-xs font-600 transition-all duration-300 ${
                            step >= s
                              ? 'bg-accent text-white' :'bg-surface text-muted border border-border'
                          }`}
                          style={{ borderRadius: '2px' }}
                        >
                          {step > s ? '✓' : s}
                        </div>
                        <span className={`font-body text-sm hidden sm:block ${step >= s ? 'text-foreground' : 'text-muted'}`}>
                          {s === 1 ? '項目類型' : s === 2 ? '空間詳情' : '聯繫資料'}
                        </span>
                      </div>
                      {i < 2 && (
                        <div className={`flex-1 h-px mx-4 transition-all duration-500 ${step > s ? 'bg-accent' : 'bg-border'}`} />
                      )}
                    </React.Fragment>
                  ))}
                </div>

                <form onSubmit={handleSubmit}>
                  {/* Step 1: Project Type */}
                  {step === 1 && (
                    <div
                      style={{
                        opacity: formRef.inView ? 1 : 0,
                        transform: formRef.inView ? 'translateY(0)' : 'translateY(30px)',
                        transition: 'opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s',
                      }}
                    >
                      <h2 className="font-heading text-2xl font-700 text-primary mb-2">
                        您的項目類型是？
                      </h2>
                      <p className="font-body text-sm text-muted mb-8">請選擇最符合您需求的項目類型</p>

                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-10">
                        {projectTypes.map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() => update('projectType', type)}
                            className={`p-4 text-left border transition-all duration-200 hover-shine ${
                              formData.projectType === type
                                ? 'border-accent bg-accent/5 text-primary' :'border-border bg-surface text-secondary hover:border-accent/50'
                            }`}
                            style={{ borderRadius: '2px' }}
                          >
                            <span className="font-body text-sm font-500">{type}</span>
                            {formData.projectType === type && (
                              <span className="font-mono text-xs text-accent ml-2">✓</span>
                            )}
                          </button>
                        ))}
                      </div>

                      <div className="mb-8">
                        <label className="font-body text-sm font-500 text-primary block mb-2">
                          空間類型補充說明（選填）
                        </label>
                        <textarea
                          className="form-input resize-none"
                          rows={3}
                          placeholder="例如：3房2廳，約800呎，希望有開放式廚房……"
                          value={formData.spaceType}
                          onChange={(e) => update('spaceType', e.target.value)}
                        />
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => setStep(2)}
                          disabled={!canProceedStep1}
                          className={`btn-accent ${!canProceedStep1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          下一步
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 2: Space Details */}
                  {step === 2 && (
                    <div>
                      <h2 className="font-heading text-2xl font-700 text-primary mb-2">
                        空間詳情
                      </h2>
                      <p className="font-body text-sm text-muted mb-8">幫助我們更好地了解您的項目規模</p>

                      <div className="space-y-8">
                        {/* Space size */}
                        <div>
                          <label className="font-body text-sm font-600 text-primary block mb-3">空間面積</label>
                          <div className="flex flex-wrap gap-3">
                            {spaceSizes.map((size) => (
                              <button
                                key={size}
                                type="button"
                                onClick={() => update('spaceSize', size)}
                                className={`px-4 py-2.5 border text-sm font-body font-500 transition-all duration-200 ${
                                  formData.spaceSize === size
                                    ? 'border-accent bg-accent/5 text-primary' :'border-border text-secondary hover:border-accent/50'
                                }`}
                                style={{ borderRadius: '2px' }}
                              >
                                {size}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Budget */}
                        <div>
                          <label className="font-body text-sm font-600 text-primary block mb-3">預算範圍</label>
                          <div className="flex flex-wrap gap-3">
                            {budgets.map((b) => (
                              <button
                                key={b}
                                type="button"
                                onClick={() => update('budget', b)}
                                className={`px-4 py-2.5 border text-sm font-body font-500 transition-all duration-200 ${
                                  formData.budget === b
                                    ? 'border-accent bg-accent/5 text-primary' :'border-border text-secondary hover:border-accent/50'
                                }`}
                                style={{ borderRadius: '2px' }}
                              >
                                {b}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Timeline */}
                        <div>
                          <label className="font-body text-sm font-600 text-primary block mb-3">期望施工時間</label>
                          <div className="flex flex-wrap gap-3">
                            {timelines.map((t) => (
                              <button
                                key={t}
                                type="button"
                                onClick={() => update('timeline', t)}
                                className={`px-4 py-2.5 border text-sm font-body font-500 transition-all duration-200 ${
                                  formData.timeline === t
                                    ? 'border-accent bg-accent/5 text-primary' :'border-border text-secondary hover:border-accent/50'
                                }`}
                                style={{ borderRadius: '2px' }}
                              >
                                {t}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-between mt-10">
                        <button
                          type="button"
                          onClick={() => setStep(1)}
                          className="btn-outline"
                        >
                          ← 上一步
                        </button>
                        <button
                          type="button"
                          onClick={() => setStep(3)}
                          disabled={!canProceedStep2}
                          className={`btn-accent ${!canProceedStep2 ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          下一步 →
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Contact Info */}
                  {step === 3 && (
                    <div>
                      <h2 className="font-heading text-2xl font-700 text-primary mb-2">
                        您的聯繫資料
                      </h2>
                      <p className="font-body text-sm text-muted mb-8">
                        我們將在2個工作日內與您聯繫，安排免費初步諮詢。
                      </p>

                      <div className="space-y-5">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                          <div>
                            <label className="font-body text-sm font-600 text-primary block mb-2">
                              姓名 <span className="text-accent">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-input"
                              placeholder="您的姓名"
                              value={formData.name}
                              onChange={(e) => update('name', e.target.value)}
                              required
                            />
                          </div>
                          <div>
                            <label className="font-body text-sm font-600 text-primary block mb-2">
                              電話 <span className="text-accent">*</span>
                            </label>
                            <input
                              type="tel"
                              className="form-input"
                              placeholder="+852 XXXX XXXX"
                              value={formData.phone}
                              onChange={(e) => update('phone', e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        <div>
                          <label className="font-body text-sm font-600 text-primary block mb-2">
                            電郵地址 <span className="text-accent">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-input"
                            placeholder="your@email.com"
                            value={formData.email}
                            onChange={(e) => update('email', e.target.value)}
                            required
                          />
                        </div>

                        <div>
                          <label className="font-body text-sm font-600 text-primary block mb-2">
                            項目地址（選填）
                          </label>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="例如：沙田第一城、大圍……"
                            value={formData.address}
                            onChange={(e) => update('address', e.target.value)}
                          />
                        </div>

                        <div>
                          <label className="font-body text-sm font-600 text-primary block mb-2">
                            其他備注（選填）
                          </label>
                          <textarea
                            className="form-input resize-none"
                            rows={4}
                            placeholder="您有任何特別需求或想法，歡迎告訴我們……"
                            value={formData.message}
                            onChange={(e) => update('message', e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Summary */}
                      <div className="mt-8 p-5 bg-surface border border-border" style={{ borderRadius: '4px' }}>
                        <p className="font-mono text-xs text-muted uppercase tracking-widest mb-3">諮詢摘要</p>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          {[
                            { label: '項目類型', value: formData.projectType },
                            { label: '空間面積', value: formData.spaceSize },
                            { label: '預算範圍', value: formData.budget },
                            { label: '施工時間', value: formData.timeline },
                          ].map((item) => (
                            <div key={item.label}>
                              <span className="font-mono text-xs text-muted">{item.label}：</span>
                              <span className="font-body text-sm text-primary ml-1">{item.value || '—'}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="flex justify-between mt-8">
                        <button type="button" onClick={() => setStep(2)} className="btn-outline">
                          ← 上一步
                        </button>
                        <button
                          type="submit"
                          disabled={!canSubmit}
                          className={`btn-accent ${!canSubmit ? 'opacity-40 cursor-not-allowed' : ''}`}
                        >
                          提交諮詢申請
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                </form>
              </>
            ) : (
              /* Success state */
              <div className="text-center py-20">
                <div className="w-16 h-16 bg-accent/10 flex items-center justify-center mx-auto mb-6" style={{ borderRadius: '4px' }}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#C9A96E" strokeWidth="1.5">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="font-heading text-2xl font-700 text-primary mb-3">
                  感謝您的申請！
                </h2>
                <p className="font-body text-base text-secondary mb-2">
                  我們已收到您的諮詢申請，將在 <strong>2個工作日內</strong> 與您聯繫。
                </p>
                <p className="font-body text-sm text-muted mb-8">
                  如有緊急需求，歡迎直接電郵聯繫：macklavelle14@gmail.com
                </p>
                <div className="flex gap-4 justify-center">
                  <Link href="/homepage" className="btn-outline">
                    返回首頁
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why consult */}
      <section className="py-16 bg-surface border-t border-border">
        <div className="max-w-container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { icon: '◎', title: '免費初步諮詢', desc: '首次諮詢完全免費，無需任何承諾。' },
              { icon: '◈', title: '2日內回覆', desc: '提交後2個工作日內，我們主動聯繫您。' },
              { icon: '◇', title: '專屬方案規劃', desc: '根據您的需求量身定制，不套用模板。' },
            ].map((item) => (
              <div key={item.title} className="p-6">
                <span className="text-3xl text-accent block mb-4">{item.icon}</span>
                <h4 className="font-heading text-base font-700 text-primary mb-2">{item.title}</h4>
                <p className="font-body text-sm text-secondary">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}